import { Popup, ToolbarItem } from 'devextreme-react/popup';
import { Button, LoadIndicator, ScrollView, TextArea, Tooltip } from 'devextreme-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { EAFacturarEnableOptions, EFEVRIPSStatus, FEVRIPSNotification } from '../../../../types/types.preorders';
import { newToast } from '../../../../components';
import { useScreenSize } from '../../../../utils/media-query';
import { useApiBilling } from '../../../../api/api.billing';
import { errorInvoice, successInvoice, warningInvoice, PdfIcon, XmlFile } from '../../../../images/icons';
import { connectToFEVRIPSHub, subscribeToPreorden, unsubscribeFromPreorden, isConnected, updateNotificationCallback } from '../../../../signalR/Controllers/FEVRIPS';
import './ModalFEVRIPS.scss';
import { useAppSelector } from '../../../../contexts/hooks';

// Mapeo de códigos de error de AFacturar
const AFACTURAR_ERROR_CODES: Record<number, string> = {
  0: 'Error desconocido',
  6: 'Error de timeout en conexión',
  7: 'Error de servidor',
  12: 'Error de conexión SSL',
  15: 'Error retornado por la DIAN',
  28: 'Tiempo de espera agotado (más de 60 seg)',
  99: 'Respuesta inesperada del servidor',
  400: 'Solicitud incorrecta',
  401: 'No autorizado',
  403: 'Prohibido',
  404: 'Recurso no encontrado',
  405: 'Método no permitido',
  409: 'Conflicto en la solicitud',
  415: 'Tipo de medio no admitido',
  422: 'Entidad no procesable (errores semánticos)',
  429: 'Límite de envío excedido',
  500: 'Error interno del servidor',
  503: 'Servicio no disponible',
};

// Interfaces para respuestas de AFacturar
interface AFacturarResultItem {
  propiedad?: string;
  descripcion?: string;
}

// Resultado puede ser un objeto con info de factura (en caso de error DIAN código 15)
interface AFacturarResultInfo {
  cufe?: string;
  id_factura?: string;
  url_representacion_qr_svg?: string;
  qr_url_imagen_base_64?: string;
  url_representacion_xml?: string;
}

interface AFacturarResponse {
  codigo_error?: number;
  descripcion_error?: string;
  resultado?: (AFacturarResultItem[] | AFacturarResultItem)[] | AFacturarResultInfo | null;
  ErrorMessage_DIAN?: string;
  validado?: boolean;
  CUFE?: string;
  entorno?: string;
}

// Función para decodificar caracteres Unicode escapados
const decodeUnicodeEscapes = (str: string): string => {
  if (!str || typeof str !== 'string') return str || '';
  return str.replace(/\\u([0-9A-Fa-f]{4})/g, (_, code) => String.fromCharCode(parseInt(code, 16)));
};

// Función para parsear la respuesta JSON de AFacturar
const parseAFacturarResponse = (jsonResponse: string | null | undefined): AFacturarResponse | null => {
  if (!jsonResponse) return null;
  try {
    // Primero decodificar caracteres Unicode escapados
    const decoded = decodeUnicodeEscapes(jsonResponse);
    return JSON.parse(decoded);
  } catch {
    // Si falla el parseo, intentar sin decodificar
    try {
      return JSON.parse(jsonResponse);
    } catch {
      return null;
    }
  }
};

// Función auxiliar para extraer items de error del resultado
const extractErrorItems = (resultado: AFacturarResponse['resultado']): { property: string; description: string }[] => {
  const errorItems: { property: string; description: string }[] = [];

  if (!resultado) return errorItems;

  // Si resultado es un objeto (no array) - típico del código 15, no tiene items de error
  if (!Array.isArray(resultado)) return errorItems;

  const processItem = (item: unknown) => {
    if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
      const resultItem = item as AFacturarResultItem;
      if (resultItem.propiedad || resultItem.descripcion) {
        errorItems.push({
          property: resultItem.propiedad || '',
          description: resultItem.descripcion || '',
        });
      }
    }
  };

  resultado.forEach((item) => {
    if (Array.isArray(item)) {
      // Formato: [[{propiedad, descripcion}, ...]]
      item.forEach((subItem) => processItem(subItem));
    } else {
      // Formato: [{propiedad, descripcion}, ...]
      processItem(item);
    }
  });

  return errorItems;
};

// Función para parsear mensajes de error DIAN (pueden venir concatenados)
const parseDIANErrors = (errorMessage: string | undefined): { rule: string; message: string }[] => {
  if (!errorMessage) return [];

  const errors: { rule: string; message: string }[] = [];
  // Formato: "Regla: FAU06, Rechazo: mensaje.Regla: FAU02, Rechazo: mensaje"
  const regex = /Regla:\s*([^,]+),\s*Rechazo:\s*([^.]+(?:\.[^R]|[^.])*)/g;
  let match;

  while ((match = regex.exec(errorMessage)) !== null) {
    errors.push({
      rule: match[1].trim(),
      message: match[2].trim(),
    });
  }

  // Si no se encontraron con el regex, devolver el mensaje completo como un solo error
  if (errors.length === 0 && errorMessage.trim()) {
    errors.push({ rule: '', message: errorMessage });
  }

  return errors;
};

// Componente para mostrar errores de AFacturar de forma estructurada
const AFacturarErrorDisplay = ({ jsonResponse, errorMessage }: { jsonResponse: string | null | undefined; errorMessage: string | null | undefined }) => {
  // Intentar parsear jsonResponse primero, si no, intentar parsear errorMessage (puede contener JSON)
  let parsedResponse = parseAFacturarResponse(jsonResponse);

  // Si no hay respuesta parseada de jsonResponse, intentar parsear errorMessage
  if (!parsedResponse && errorMessage) {
    // Verificar si errorMessage parece ser un JSON (empieza con {)
    const trimmedError = errorMessage.trim();
    if (trimmedError.startsWith('{')) {
      parsedResponse = parseAFacturarResponse(errorMessage);
    }
  }

  // Si no hay respuesta parseada, mostrar el mensaje de error simple
  if (!parsedResponse) {
    return errorMessage ? <p className='error-simple'>{errorMessage}</p> : null;
  }

  const { codigo_error, descripcion_error, ErrorMessage_DIAN, entorno } = parsedResponse;
  const errorCodeDescription = codigo_error !== undefined ? AFACTURAR_ERROR_CODES[codigo_error] : null;

  // Procesar resultado según el formato
  const errorItems = extractErrorItems(parsedResponse.resultado);

  // Parsear errores DIAN
  const dianErrors = parseDIANErrors(ErrorMessage_DIAN);

  return (
    <div className='afacturar-error-detail'>
      {/* Código de error y descripción principal */}
      <div className='error-header'>
        {codigo_error !== undefined && (
          <span className='error-code'>
            Código {codigo_error}
            {errorCodeDescription && ` - ${errorCodeDescription}`}
          </span>
        )}
        {descripcion_error && <p className='error-description'>{descripcion_error}</p>}
        {entorno && <p className='error-entorno'>{entorno}</p>}
      </div>

      {/* Errores DIAN estructurados */}
      {dianErrors.length > 0 && (
        <div className='error-dian'>
          <strong>Errores DIAN:</strong>
          <ul>
            {dianErrors.map((error, index) => (
              <li key={index}>
                {error.rule && <span className='dian-rule'>[{error.rule}]</span>}
                <span className='dian-message'>{error.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Lista de errores detallados */}
      {errorItems.length > 0 && (
        <div className='error-list'>
          <strong>Detalles:</strong>
          <ul>
            {errorItems.map((item, index) => (
              <li key={index}>
                {item.property && <span className='error-property'>{decodeUnicodeEscapes(item.property)}:</span>}
                <span className='error-item-description'>{decodeUnicodeEscapes(item.description)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Fallback al mensaje de error simple si no hay detalles */}
      {errorItems.length === 0 && !descripcion_error && !ErrorMessage_DIAN && errorMessage && <p className='error-simple'>{errorMessage}</p>}
    </div>
  );
};

interface ModalFEVRIPSProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  idPreorden: number;
  sedeId: number | null | undefined;
  preorderData: any;
  enableOptions: EAFacturarEnableOptions;
  onRefreshData?: () => void;
}

interface NotificationLog extends FEVRIPSNotification {
  id: string;
}

// Componente para mostrar el badge de estado
const StatusBadge = ({ status, label }: { status: EFEVRIPSStatus | null | undefined; label: string }) => {
  const getStatusConfig = () => {
    switch (status) {
      case EFEVRIPSStatus.Pending:
        return { className: 'status-pending', icon: warningInvoice, text: 'Preparado' };
      case EFEVRIPSStatus.Sending:
        return { className: 'status-sending', icon: warningInvoice, text: 'Enviando' };
      case EFEVRIPSStatus.Success:
        return { className: 'status-success', icon: successInvoice, text: 'Exitoso' };
      case EFEVRIPSStatus.Error:
        return { className: 'status-error', icon: errorInvoice, text: 'Error' };
      default:
        return { className: 'status-none', icon: warningInvoice, text: 'Sin procesar' };
    }
  };

  const config = getStatusConfig();

  return (
    <div className={`fevrips-status-badge ${config.className}`}>
      <img src={config.icon} alt='' className='status-icon-img' />
      <span className='status-label'>{label}</span>
      <span className='status-text'>{config.text}</span>
    </div>
  );
};

// Componente para mostrar una notificación en el timeline
const NotificationItem = ({ notification }: { notification: NotificationLog }) => {
  const getTypeConfig = () => {
    switch (notification.Type) {
      case 'success':
        return { className: 'notification-success', icon: successInvoice };
      case 'error':
        return { className: 'notification-error', icon: errorInvoice };
      case 'warning':
        return { className: 'notification-warning', icon: warningInvoice };
      case 'info':
      default:
        return { className: 'notification-info', icon: warningInvoice };
    }
  };

  const config = getTypeConfig();
  const timestamp = new Date(notification.Timestamp).toLocaleTimeString('es-CO');

  return (
    <div className={`notification-item ${config.className}`}>
      <img src={config.icon} alt='' className='notification-icon-img' />
      <div className='notification-content'>
        <div className='notification-header'>
          <span className='notification-title'>{notification.Title}</span>
          <span className='notification-time'>{timestamp}</span>
        </div>
        <div className='notification-message'>{notification.Message}</div>
      </div>
    </div>
  );
};

const ModalFEVRIPS = ({ visible, setVisible, idPreorden, sedeId, preorderData, enableOptions, onRefreshData }: ModalFEVRIPSProps) => {
  const { isSmall, isXSmall } = useScreenSize();
  const { createFEV, CreateRIPS } = useApiBilling();

  const [notifications, setNotifications] = useState<NotificationLog[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [observation, setObservation] = useState('');
  const [hubConnected, setHubConnected] = useState(false);

  // Estados de FEV y RIPS de la preorden
  const fevStatus = preorderData?.POrd_FEV_Estado as EFEVRIPSStatus | null;
  const ripsStatus = preorderData?.POrd_RIPS_Estado as EFEVRIPSStatus | null;
  const fevCUFE = preorderData?.POrd_FEV_CUFE;
  const fevNumero = preorderData?.POrd_FEV;
  const fevError = preorderData?.POrd_FEV_MensajeError;
  const fevJSONResponse = preorderData?.POrd_FEV_JSONResponse;
  const ripsError = preorderData?.POrd_RIPS_MensajeError;
  const ripsJSONResponse = preorderData?.POrd_RIPS_JSONResponse;
  const ripsCUV = preorderData?.POrd_RIPS_CUV;
  const fevQR = preorderData?.POrd_FEV_QR;
  const fevUrlPDF = preorderData?.POrd_FEV_UrlPDF;
  const fevXML = preorderData?.POrd_FEV_XML;
  const hasOrder = preorderData?.POrd_Ord_Id != null;
  const Prm_FactFEVRIPS_C = useAppSelector((x) => x.userInfo.Permissions?.Prm_FactFEVRIPS);
  // Determinar si se puede crear FEV
  const canCreateFEV = useMemo(() => {
    if (!hasOrder) return false;
    if (isProcessing) return false;
    // Si ya tiene CUFE, la factura ya fue validada por DIAN - NO se puede regenerar
    if (fevCUFE) return false;
    // Se puede crear si no hay estado, está en error, está pendiente
    return fevStatus === null || fevStatus === undefined || fevStatus === EFEVRIPSStatus.Error || fevStatus === EFEVRIPSStatus.Pending;
  }, [hasOrder, fevStatus, fevCUFE, isProcessing]);

  // Determinar si se puede crear solo RIPS
  const canCreateRIPS = useMemo(() => {
    if (!hasOrder) return false;
    if (isProcessing) return false;
    if (enableOptions !== EAFacturarEnableOptions.FEV_RIPS) return false;
    // Solo se pueden crear RIPS si la FEV está exitosa y RIPS no está exitoso
    return fevStatus === EFEVRIPSStatus.Success && (ripsStatus === null || ripsStatus === undefined || ripsStatus === EFEVRIPSStatus.Error);
  }, [hasOrder, fevStatus, ripsStatus, enableOptions, isProcessing]);

  // Handler para notificaciones de SignalR
  const handleNotification = useCallback(
    (notification: FEVRIPSNotification) => {
      // Solo procesar notificaciones de esta preorden
      if (notification.IdPreorden !== idPreorden) return;

      const newNotification: NotificationLog = {
        ...notification,
        id: `${Date.now()}-${Math.random()}`,
      };

      setNotifications((prev) => [newNotification, ...prev]);

      // Mostrar toast según el tipo
      switch (notification.Type) {
        case 'success':
          newToast('SUCCESS', notification.Message, 4);
          setIsProcessing(false);
          break;
        case 'error':
          newToast('ERROR', notification.Message, 6);
          setIsProcessing(false);
          break;
        case 'warning':
          newToast('WARNING', notification.Message, 4);
          break;
      }

      // Si es un mensaje final (éxito o error), refrescar datos y detener loading
      if (notification.Title === 'Proceso completado' || notification.Type === 'error' || notification.Type === 'success') {
        setIsProcessing(false);
        onRefreshData?.();
      }
    },
    [idPreorden, onRefreshData]
  );

  // Conectar al Hub de SignalR cuando el modal se abre
  useEffect(() => {
    if (visible && idPreorden) {
      if (!isConnected()) {
        connectToFEVRIPSHub(handleNotification, sedeId)
          .then(() => {
            setHubConnected(true);
            subscribeToPreorden(idPreorden);
          })
          .catch(() => {
            // Silently fail - continuar sin notificaciones en tiempo real
          });
      } else {
        updateNotificationCallback(handleNotification);
        subscribeToPreorden(idPreorden);
        setHubConnected(true);
      }
    }

    return () => {
      if (idPreorden) {
        unsubscribeFromPreorden(idPreorden);
      }
    };
  }, [visible, idPreorden, sedeId, handleNotification]);

  // Mantener el callback actualizado cuando cambia
  useEffect(() => {
    if (hubConnected) {
      updateNotificationCallback(handleNotification);
    }
  }, [handleNotification, hubConnected]);

  // Limpiar notificaciones al cerrar
  useEffect(() => {
    if (!visible) {
      setNotifications([]);
      setObservation('');
    }
  }, [visible]);

  // Crear FEV (o FEV + RIPS según la configuración)
  const handleCreateFEV = useCallback(async () => {
    if (!canCreateFEV) return;

    setIsProcessing(true);

    // Agregar notificación local
    const startNotification: NotificationLog = {
      id: `local-${Date.now()}`,
      Type: 'info',
      Title: 'Iniciando proceso',
      Message: enableOptions === EAFacturarEnableOptions.FEV_RIPS ? 'Generando Factura Electrónica y RIPS...' : 'Generando Factura Electrónica...',
      IdPreorden: idPreorden,
      Timestamp: new Date().toISOString(),
    };
    setNotifications((prev) => [startNotification, ...prev]);

    try {
      await createFEV(idPreorden, observation);
      // Las notificaciones llegarán por SignalR
    } catch (error: any) {
      setIsProcessing(false);
      newToast('ERROR', error.message || 'Error al generar la factura electrónica', 5);
      onRefreshData?.(); // Refrescar datos para actualizar estado
    }
  }, [canCreateFEV, idPreorden, observation, createFEV, enableOptions, onRefreshData]);

  // Crear solo RIPS
  const handleCreateRIPS = useCallback(async () => {
    if (!canCreateRIPS) return;

    setIsProcessing(true);

    const startNotification: NotificationLog = {
      id: `local-${Date.now()}`,
      Type: 'info',
      Title: 'Iniciando proceso',
      Message: 'Generando RIPS...',
      IdPreorden: idPreorden,
      Timestamp: new Date().toISOString(),
    };
    setNotifications((prev) => [startNotification, ...prev]);

    try {
      await CreateRIPS(idPreorden);
      // Las notificaciones llegarán por SignalR
    } catch (error: any) {
      setIsProcessing(false);
      newToast('ERROR', error.message || 'Error al generar los RIPS', 5);
      onRefreshData?.(); // Refrescar datos para actualizar estado
    }
  }, [canCreateRIPS, idPreorden, CreateRIPS, onRefreshData]);

  const onClose = () => {
    setVisible(false);
  };

  const getModalTitle = () => {
    if (enableOptions === EAFacturarEnableOptions.FEV_RIPS) {
      return 'Facturación Electrónica y RIPS';
    }
    return 'Facturación Electrónica';
  };

  return (
    <Popup visible={visible} onHiding={onClose} showCloseButton={true} showTitle={true} title={getModalTitle()} width={isSmall || isXSmall ? '95%' : 600} height={isSmall || isXSmall ? '90%' : 'auto'} maxHeight='80vh' dragEnabled={true} wrapperAttr={{ class: 'fevrips-modal' }}>
      <ScrollView showScrollbar='onScroll'>
        <div className='fevrips-container'>
          {/* Sección de Estado Actual */}
          <div className='fevrips-status-section'>
            <div className='status-badges'>
              <StatusBadge status={fevStatus} label='FEV' />
              {enableOptions === EAFacturarEnableOptions.FEV_RIPS && <StatusBadge status={ripsStatus} label='RIPS' />}
            </div>

            {/* Contenido dividido: Info + QR */}
            {fevStatus === EFEVRIPSStatus.Success && (
              <div className='fevrips-content-split'>
                {/* Lado izquierdo: Información */}
                <div className='fevrips-info-side'>
                  {fevNumero && (
                    <div className='fevrips-info-row compact'>
                      <span className='info-label'>No. Factura:</span>
                      <span className='info-value'>{fevNumero}</span>
                    </div>
                  )}

                  {fevCUFE && (
                    <div className='fevrips-info-row compact'>
                      <span className='info-label'>CUFE:</span>
                      <span className='info-value cufe'>{fevCUFE}</span>
                    </div>
                  )}

                  {ripsCUV && enableOptions === EAFacturarEnableOptions.FEV_RIPS && (
                    <div className='fevrips-info-row compact'>
                      <span className='info-label'>CUV RIPS:</span>
                      <span className='info-value'>{ripsCUV}</span>
                    </div>
                  )}

                  {/* Links de descarga */}
                  <div className='fevrips-links'>
                    {fevUrlPDF && (
                      <a href={fevUrlPDF} target='_blank' rel='noopener noreferrer' className='fevrips-link pdf'>
                        <img src={PdfIcon} alt='' className='link-icon-img' />
                        Ver Factura
                      </a>
                    )}
                    {fevXML && (
                      <a href={fevXML} target='_blank' rel='noopener noreferrer' className='fevrips-link xml' download>
                        <img src={XmlFile} alt='' className='link-icon-img' />
                        Descargar XML
                      </a>
                    )}
                  </div>
                </div>

                {/* Lado derecho: QR */}
                {fevQR && (
                  <div className='fevrips-qr-side'>
                    <img src={fevQR} alt='QR Factura' className='qr-image' />
                  </div>
                )}
              </div>
            )}

            {/* Info sin QR (cuando no hay éxito) */}
            {fevStatus !== EFEVRIPSStatus.Success && (
              <>
                {fevNumero && (
                  <div className='fevrips-info-row'>
                    <span className='info-label'>No. Factura:</span>
                    <span className='info-value'>{fevNumero}</span>
                  </div>
                )}

                {fevCUFE && (
                  <div className='fevrips-info-row'>
                    <span className='info-label'>CUFE:</span>
                    <span className='info-value cufe'>{fevCUFE}</span>
                  </div>
                )}

                {ripsCUV && enableOptions === EAFacturarEnableOptions.FEV_RIPS && (
                  <div className='fevrips-info-row'>
                    <span className='info-label'>CUV RIPS:</span>
                    <span className='info-value'>{ripsCUV}</span>
                  </div>
                )}
              </>
            )}

            {/* Mensajes de Error */}
            {fevStatus === EFEVRIPSStatus.Error && (fevError || fevJSONResponse) && (
              <div className='fevrips-error-box'>
                <img src={errorInvoice} alt='Error' className='error-icon-img' />
                <div className='error-content'>
                  <strong>Error FEV:</strong>
                  <AFacturarErrorDisplay jsonResponse={fevJSONResponse} errorMessage={fevError} />
                </div>
              </div>
            )}

            {ripsStatus === EFEVRIPSStatus.Error && (ripsError || ripsJSONResponse) && enableOptions === EAFacturarEnableOptions.FEV_RIPS && (
              <div className='fevrips-error-box'>
                <img src={errorInvoice} alt='Error' className='error-icon-img' />
                <div className='error-content'>
                  <strong>Error RIPS:</strong>
                  <AFacturarErrorDisplay jsonResponse={ripsJSONResponse} errorMessage={ripsError} />
                </div>
              </div>
            )}
          </div>

          {/* Advertencia si no tiene orden */}
          {!hasOrder && (
            <div className='fevrips-warning'>
              <img src={warningInvoice} alt='Advertencia' className='warning-icon-img' />
              <span>Esta preorden no tiene una orden asociada. Debe crear una orden primero para generar la factura electrónica.</span>
            </div>
          )}

          {/* Campo de Observaciones */}
          {hasOrder && (canCreateFEV || canCreateRIPS) && (
            <div className='fevrips-observation-section'>
              <TextArea label='Observaciones (opcional)' labelMode='floating' stylingMode='outlined' value={observation} onValueChanged={(e) => setObservation(e.value)} height={60} disabled={isProcessing} />
            </div>
          )}

          {/* Sección de Acciones */}
          {hasOrder && (
            <div className='fevrips-actions'>
              <Button id='btnCreateFEV' text={enableOptions === EAFacturarEnableOptions.FEV_RIPS ? 'Generar FEV + RIPS' : 'Generar Factura'} type='default' visible={Prm_FactFEVRIPS_C} stylingMode='contained' disabled={!canCreateFEV} onClick={handleCreateFEV}>
                {isProcessing && fevStatus !== EFEVRIPSStatus.Success && <LoadIndicator width={16} height={16} />}
              </Button>

              {enableOptions === EAFacturarEnableOptions.FEV_RIPS && (
                <>
                  <Button id='btnCreateRIPS' text='Generar solo RIPS' type='normal' stylingMode='outlined' disabled={!canCreateRIPS} visible={Prm_FactFEVRIPS_C} onClick={handleCreateRIPS}>
                    {isProcessing && fevStatus === EFEVRIPSStatus.Success && <LoadIndicator width={16} height={16} />}
                  </Button>
                  <Tooltip target='#btnCreateRIPS' showEvent='mouseenter' hideEvent='mouseleave' position='top'>
                    {canCreateRIPS ? 'Generar RIPS para esta factura' : 'Debe generar la factura electrónica primero para poder generar los RIPS'}
                  </Tooltip>
                </>
              )}
            </div>
          )}

          {/* Timeline de Notificaciones */}
          {notifications.length > 0 && (
            <div className='fevrips-notifications-section'>
              <div className='notifications-header'>
                <h4>Actividad</h4>
              </div>
              <div className='notifications-timeline'>
                {notifications.map((notification) => (
                  <NotificationItem key={notification.id} notification={notification} />
                ))}
              </div>
            </div>
          )}
        </div>
      </ScrollView>

      <ToolbarItem toolbar='bottom' location='after'>
        <Button text='Cerrar' onClick={onClose} />
      </ToolbarItem>
    </Popup>
  );
};

export default ModalFEVRIPS;
